import { z } from 'zod';
import { PlainDate } from './common.js';
import { AudiencePolicy } from './feedback.js';

/**
 * AI features (APP-021, APP-048): Admin › AI (SYS7), the Help chat (AI4), Ask the data (RPT5).
 * The email and Teams assistant has no screens of its own: it answers by email and leaves its
 * thread on the award record.
 */

export const AiProvider = z.enum(['anthropic', 'bedrock_govcloud']);
export type AiProvider = z.infer<typeof AiProvider>;
/**
 * Bedrock's regions (APP-107 §2.2; the same list as the domain's `AWS_REGIONS`). GovCloud always;
 * the commercial ones only where the environment allows them (the view's `bedrock.regions`).
 */
export const AwsRegion = z.enum([
  'us-gov-west-1',
  'us-gov-east-1',
  'us-east-1',
  'us-east-2',
  'us-west-2',
]);
export type AwsRegion = z.infer<typeof AwsRegion>;
/**
 * Why a connection test failed (APP-107 §2.6): a fixed code the card words, never the provider's
 * own text.
 */
export const AiConnectionFailure = z.enum([
  'invalid_key',
  'permission',
  'model_not_found',
  'timeout',
  'network',
  'unexpected_reply',
  'not_configured',
  'provider_not_allowed',
]);
export type AiConnectionFailure = z.infer<typeof AiConnectionFailure>;
/**
 * Every code an Admin › AI change can be refused with: a failed test's (Save answers 422 with it)
 * and the ones the build added (APP-107 §2.6). The card words each one.
 */
export const AiAdminProblemCode = z.enum([
  ...AiConnectionFailure.options,
  'ai_changed',
  'ai_key_store_unavailable',
  'ai_model_untested',
  'ai_not_connected',
  'ai_model_unavailable',
  'ai_no_key_store',
  // Voice for feedback can't be ticked until Test speech passes (APP-104 §7).
  'speech_not_connected',
]);
export type AiAdminProblemCode = z.infer<typeof AiAdminProblemCode>;
/** Where voice feedback's speech goes (APP-107 §2.4): Azure AI Speech, or the local stand-in. */
export const AiSpeechProvider = z.enum(['azure_speech', 'stand_in']);
export type AiSpeechProvider = z.infer<typeof AiSpeechProvider>;
/**
 * Why Test speech failed: a fixed code the Speech card words, never the service's own text.
 * `not_configured`: no endpoint here. `permission`: the sign-in or the role was refused.
 * `quota`: too many requests. `network`: unreachable. `timeout`: no clean stop in time.
 */
export const AiSpeechTestFailure = z.enum([
  'not_configured',
  'permission',
  'quota',
  'network',
  'timeout',
  'internal',
]);
export type AiSpeechTestFailure = z.infer<typeof AiSpeechTestFailure>;
export const AiFeatureKey = z.enum([
  'help',
  'email',
  'teams',
  'ask_data',
  'feedback_summary',
  'feedback_voice',
  'help_voice',
  'feedback_assistant',
  // APP-263 (APP-191 D10 C): Ask the data's Automated summary, the one feature that sends figures.
  'ask_summary',
]);
export type AiFeatureKey = z.infer<typeof AiFeatureKey>;

const Changed = z.object({
  by: z.object({ id: z.string(), name: z.string() }).nullable(),
  at: z.string(),
});
export type AiChanged = z.infer<typeof Changed>;

/** An app path the UI may link to (`/recognize/taa`), never an outside URL. */
export const AppLink = z.object({
  label: z.string(),
  path: z.string().regex(/^\/(?:[a-z0-9-]+(?:\/[a-z0-9-]+)*)?(?:\?[a-z0-9=&-]*)?$/),
});
export type AppLink = z.infer<typeof AppLink>;

// ---- Which AI features I can use (shell) ---------------------------------------------------

/** Whether each in-app AI feature is usable by me right now (switches, key, audience, role). */
export const AiStatus = z.object({
  /** Help shows Ask (chat) next to Browse help. Off: Browse help and the contact only. */
  helpChat: z.boolean(),
  /** Insights › Ask the data takes new questions. Saved questions re-run either way. */
  askData: z.boolean(),
  /** Requests left today across every AI feature, or null when unlimited for me. */
  remainingToday: z.number().int().nonnegative().nullable(),
  /**
   * Help chat's microphone (APP-124): Voice for Help chat is offered to me now, even with today's
   * voice time used up, so the button can say so. Optional for older readers.
   */
  helpVoice: z.boolean().optional(),
  /** Voice time left today, in seconds, shared with feedback (0 when used up). Optional. */
  voiceSecondsLeft: z.number().int().nonnegative().optional(),
  /**
   * APP-263 (APP-191 D10 C): a summary asked of Ask the data comes with an Automated summary, a
   * paragraph the model writes from the answer's totals. False while the master switch, Ask the
   * data or the Automated summary is off, or I'm outside either audience. Optional for older readers.
   */
  askSummary: z.boolean().optional(),
});
export type AiStatus = z.infer<typeof AiStatus>;

/**
 * `POST /v1/help/voice` (APP-124): a one-use ticket for the voice socket's help `start`, good for
 * 2 minutes; null when Voice for Help chat isn't offered now (a switch, the audience, the day's
 * voice time).
 */
export const HelpVoiceTicket = z.object({ ticket: z.string().nullable() });
export type HelpVoiceTicket = z.infer<typeof HelpVoiceTicket>;
/** Its body: nothing to send. */
export const HelpVoiceTicketRequest = z.object({}).strict();

// ---- Admin › AI (SYS7) ---------------------------------------------------------------------

export const AiModel = z.object({
  /** First-party catalog ID (`claude-sonnet-5-5`); the provider's own ID is the server's business. */
  id: z.string(),
  label: z.string(),
  description: z.string(),
  recommended: z.boolean(),
  /** Kept for features already tested on it. */
  previous: z.boolean(),
  /** From the last connection test with the current key: null until tested. */
  available: z.boolean().nullable(),
});
export type AiModel = z.infer<typeof AiModel>;

export const AiKeyStatus = z.object({
  provider: AiProvider,
  set: z.boolean(),
  /** The only part of a key the app ever shows. */
  last4: z.string().length(4).nullable(),
  /** Where it is kept: the key vault in Azure, the local-only store in development. */
  store: z.enum(['key_vault', 'local']),
  /** Saved through Admin › AI; null when it was put in the vault directly. */
  lastSaved: Changed.nullable(),
  /** When the key in use was added to the vault (its version's date); null when unknown. */
  addedAt: z.string().nullable(),
  /** Whole days since `addedAt`. */
  ageDays: z.number().int().nonnegative().nullable(),
  /** 90 days or older: the card suggests rotating it. */
  rotationDue: z.boolean(),
});
export type AiKeyStatus = z.infer<typeof AiKeyStatus>;

export const AiTestResult = z.object({
  provider: AiProvider,
  ok: z.boolean(),
  latencyMs: z.number().int().nullable(),
  /** One of `AiConnectionFailure` (APP-107 §2.6); tests from before it may hold an older code. */
  errorCode: z.string().nullable(),
  /** The Bedrock region tested; null for Anthropic Direct. */
  region: z.string().nullable(),
  models: z.array(z.object({ id: z.string(), available: z.boolean() })),
  at: z.string(),
  by: z.object({ id: z.string(), name: z.string() }).nullable(),
  /** True when this test ran with what's configured now (provider, region and key version). */
  current: z.boolean(),
});
export type AiTestResult = z.infer<typeof AiTestResult>;

export const AiFeatureRow = z.object({
  key: AiFeatureKey,
  on: z.boolean(),
  /** Who can use it (APP-139): everyone, or any of these roles, or any of these named people. */
  audience: AudiencePolicy,
  /**
   * The people `audience.personIds` names, in its order, so the page can show them by name.
   * `active` is false for someone who has since left or lost directory access: they stay on the
   * list until removed, and can't use the feature meanwhile.
   */
  people: z.array(z.object({ id: z.string(), name: z.string(), active: z.boolean() })),
  /** A per-feature model override, or null for the default model. */
  model: z.string().nullable(),
  /** Whether the feature can run in this environment (Teams needs its app, I10). */
  available: z.boolean(),
  /**
   * Why not, in words, when `available` is false; or how it runs here when it is (the email
   * assistant with simulated mail only, APP-071).
   */
  unavailableReason: z.string().nullable(),
  /** On, master on and a tested key: data may go to a model for this feature. */
  effective: z.boolean(),
  requestsThisMonth: z.number().int().nonnegative(),
});
export type AiFeatureRow = z.infer<typeof AiFeatureRow>;

export const AiUsage = z.object({
  /** `YYYY-MM` in the organization's time zone. */
  month: z.string(),
  from: PlainDate,
  to: PlainDate,
  requests: z.number().int().nonnegative(),
  /** Input plus output tokens the providers reported: an estimate of the bill, not the bill. */
  tokens: z.number().int().nonnegative(),
  byFeature: z.array(z.object({ key: AiFeatureKey, requests: z.number().int().nonnegative() })),
  /** People who reached the daily limit at least once this month. */
  peopleAtLimit: z.number().int().nonnegative(),
});
export type AiUsage = z.infer<typeof AiUsage>;

/** The Speech card (APP-107 §2.4): where speech goes here and how its latest test went. */
export const AiSpeechStatus = z.object({
  /** Null: not set up in this environment (no endpoint). */
  provider: AiSpeechProvider.nullable(),
  /** The endpoint's host (`spch-….cognitiveservices.azure.us`); null for the stand-in or none. */
  endpointHost: z.string().nullable(),
  /**
   * The Azure cloud the endpoint is in, which is this deployment's (APP-159); null for the
   * stand-in or none.
   */
  cloud: z.enum(['AzureUSGovernment', 'AzureCloud']).nullable(),
  region: z.string().nullable(),
  /**
   * `not_set_up`: no endpoint. `untested`: no test of this endpoint yet. `connected` / `failed`:
   * the latest test of this endpoint. Changing the endpoint makes voice wait for a new test.
   */
  connection: z.enum(['not_set_up', 'untested', 'connected', 'failed']),
  /** The latest test of what's set up now. */
  lastTest: z
    .object({
      ok: z.boolean(),
      /** One of `AiSpeechTestFailure`; null when it passed. */
      code: z.string().nullable(),
      /** How long the session took to start. */
      latencyMs: z.number().int().nullable(),
      at: z.string(),
      by: z.object({ id: z.string(), name: z.string() }).nullable(),
    })
    .nullable(),
});
export type AiSpeechStatus = z.infer<typeof AiSpeechStatus>;

export const AiAdminView = z.object({
  master: z.object({
    on: z.boolean(),
    lastChanged: Changed.nullable(),
    /**
     * Something the switch would turn on has passed its test: the current provider's key, or
     * speech (Voice for feedback needs no model, APP-104 D21).
     */
    canTurnOn: z.boolean(),
  }),
  provider: z.object({
    kind: AiProvider,
    lastChanged: Changed.nullable(),
    /** The providers this environment offers (`AI_PROVIDERS_ALLOWED`); others show disabled. */
    allowed: z.array(AiProvider),
  }),
  /** AWS GovCloud (Bedrock): the region its endpoint follows, and the regions offered here. */
  bedrock: z.object({
    region: AwsRegion,
    regions: z.array(AwsRegion),
    lastChanged: Changed.nullable(),
  }),
  keys: z.array(AiKeyStatus),
  /**
   * `not_set_up`: no key. `untested`: no test of what's configured now. `connected` / `failed`:
   * the latest test of what's configured now.
   */
  connection: z.enum(['not_set_up', 'untested', 'connected', 'failed']),
  lastTest: AiTestResult.nullable(),
  defaultModel: z.object({
    /** As chosen; null until a System Admin picks one after a test. */
    chosen: z.string().nullable(),
    /** What features use now: the choice, or the recommended model. */
    effective: z.string(),
    lastChanged: Changed.nullable(),
  }),
  models: z.array(AiModel),
  features: z.array(AiFeatureRow),
  featuresLastChanged: Changed.nullable(),
  /**
   * The saved `ai.features` row the list shows (null: the default), sent back as `basedOn` so a
   * change made from an older copy is refused, not saved over someone else's (SENT-041).
   */
  featuresVersion: z.uuid().nullable(),
  dailyLimit: z.object({ value: z.number().int().positive(), lastChanged: Changed.nullable() }),
  historyDays: z.object({ value: z.number().int().positive(), lastChanged: Changed.nullable() }),
  /**
   * APP-258 (APP-191 D8): Ask the data history is kept this many months unless its person deletes
   * it (`ai.askDataHistoryMonths`, 1 to 120, default 24). Help keeps `historyDays`.
   */
  askDataHistoryMonths: z.object({
    value: z.number().int().positive(),
    lastChanged: Changed.nullable(),
  }),
  usage: AiUsage,
  /** Voice for feedback's speech (the Speech card). */
  speech: AiSpeechStatus,
  /** Set up AI (empty state): provider and key, a passing test, then a feature switched on. */
  setup: z.object({
    keySaved: z.boolean(),
    tested: z.boolean(),
    featureOn: z.boolean(),
  }),
});
export type AiAdminView = z.infer<typeof AiAdminView>;

/** Write-only. Never returned, logged or stored outside the key vault. */
const ApiKeyText = z
  .string()
  .trim()
  .min(20)
  .max(500)
  .regex(/^[\x21-\x7e]+$/, 'a key is printable characters without spaces');

/**
 * The AI provider card as it is on screen (APP-107 §2.2): the provider, the model, Bedrock's
 * region, and a key if one was typed (otherwise the stored key is used).
 */
const ProviderCardFields = {
  provider: AiProvider,
  /** A catalogue model ID (`claude-sonnet-5-5`). */
  model: z.string().min(1).max(100),
  /** AWS GovCloud (Bedrock) only, and required there. */
  bedrock: z.object({ region: AwsRegion }).strict().optional(),
  apiKey: ApiKeyText.optional(),
};
const regionMatchesProvider = (v: { provider: AiProvider; bedrock?: unknown }) =>
  (v.provider === 'bedrock_govcloud') === (v.bedrock !== undefined);
const REGION_RULE = {
  message: 'Choose a region for AWS GovCloud (Bedrock), and only for it',
  path: ['bedrock'],
};

/** Test connection: tests exactly what's on screen and saves nothing. */
export const TestAiProvider = z
  .object(ProviderCardFields)
  .strict()
  .refine(regionMatchesProvider, REGION_RULE);
export type TestAiProvider = z.infer<typeof TestAiProvider>;

/** Save: runs the same test first; on a pass, saves it all in one go. */
export const SaveAiProvider = z
  .object({ ...ProviderCardFields, reason: z.string().trim().max(500).optional() })
  .strict()
  .refine(regionMatchesProvider, REGION_RULE);
export type SaveAiProvider = z.infer<typeof SaveAiProvider>;

/** A test's answer: the model and its time, or a fixed reason (APP-107 §2.6). */
export const AiProviderTestResult = z.discriminatedUnion('ok', [
  z.object({ ok: z.literal(true), latencyMs: z.number().int().nonnegative(), model: z.string() }),
  z.object({ ok: z.literal(false), code: AiConnectionFailure }),
]);
export type AiProviderTestResult = z.infer<typeof AiProviderTestResult>;

/** Test speech (APP-107 §2.4): plays the built-in one-second clip; nothing to send. */
export const TestAiSpeech = z.object({}).strict();
export type TestAiSpeech = z.infer<typeof TestAiSpeech>;

/** Test speech's answer: how long the session took to start, or a fixed reason. */
export const AiSpeechTestResult = z.discriminatedUnion('ok', [
  z.object({ ok: z.literal(true), latencyMs: z.number().int().nonnegative() }),
  z.object({ ok: z.literal(false), code: AiSpeechTestFailure }),
]);
export type AiSpeechTestResult = z.infer<typeof AiSpeechTestResult>;

// ---- Help (AI4) ----------------------------------------------------------------------------

/**
 * The things on a page Help can point at with "Show me" (APP-090). One list for the server's
 * registry and the web's `data-help-anchor` attributes, so a misspelt anchor fails to compile.
 * Add an id here, mark the card on its page (`helpAnchor`), and register it, page by page, in
 * `content/help-anchors.ts`.
 */
export const HelpAnchorId = z.enum([
  // Home
  'waiting-on-you',
  'pool-card',
  'team-snapshot',
  'budget-pools',
  'recent-awards',
  'my-awards',
  // Award entry and the award record
  'budget-check',
  'paid-from',
  'entry-people',
  'award-letters',
  'where-it-is',
  'audit-trail',
  'attachments',
  'values-used',
  // An employee's award and their election
  'your-payout',
  'your-choice',
  'award-status',
  'award-documents',
  'election-choice',
  'election-compare',
  'election-estimate',
  // Approvals
  'approval-budget',
  'approval-decision',
  'approval-approve',
  'top-up',
  // The Stock Admin team's work
  'work-queue',
  'check-lines',
  'check-coding',
  'letters-preview',
  'letters-release',
  // Budget pools
  'pools-table',
  'budget-ledger',
  // Insights
  'team-everyone',
  'most-recognized',
  'insights-filters',
  'insights-export',
  // Admin › AI
  'ai-features',
  'ai-connection',
  'ai-speech',
  // System administration › Feedback (its list and each report)
  'feedback-list',
  'feedback-spoken',
  'feedback-screenshot',
  'feedback-server-log',
  // Recognize's award picker, and My drafts & submitted
  'award-picker',
  'which-fits',
  'my-drafts',
  // Past-deadline elections
  'past-deadline-list',
  'past-deadline-actions',
  // Ask the data
  'ask-conversation',
  'ask-answer',
  'ask-saved',
  // APP-191 slice 2: View as, and the export menu.
  'ask-view-as',
  'ask-export',
  // APP-191 slice 3: History, and the projects beside it.
  'ask-history',
  'ask-projects',
  // Training
  'training-lessons',
  'training-walkthrough',
  'training-check',
  // System administration's overview
  'admin-preview',
  // Executive › Values (and Settings › Values)
  'values-fmv',
  'values-split',
  'values-taa-split',
  'values-min-options',
  // APP-274: nothing on a page carries this now. Older revisions of Help's article name it, and
  // they stay readable.
  'values-requesters',
  'executive-delegates',
  // Sector settings
  'sector-delegates',
  'sector-programs',
  // Charge codes
  'charge-how-built',
  'charge-accounts',
  'charge-projects',
  'charge-org-rules',
  // Payroll, earning statements, Shareworks
  'payroll-lines',
  'payroll-codes',
  'statement-lines',
  'statement-override',
  'shareworks-grants',
  // Letters to hand out
  'handout-routed',
  // Spot nominations
  'spot-nomination',
  'spot-how',
  // My settings
  'settings-name',
  'settings-start-page',
  'settings-display',
  'settings-accessibility',
  'settings-lists-tables',
  // APP-148: a Stock Admin's Waiting on others, an Executive's or Sector Lead's Done for you, and
  // the step that asks who an action is for
  'waiting-on-others',
  'done-for-you',
  'act-for-step',
  // APP-148 R112: a Stock Admin's "Decide for a Sector Lead" card
  'decide-for-lead',
]);
export type HelpAnchorId = z.infer<typeof HelpAnchorId>;

/** Anchors no page carries any more (APP-274): older revisions of an article still name them. */
export const RETIRED_HELP_ANCHORS: readonly HelpAnchorId[] = ['values-requesters'];

/** Something on the person's current page, as Help names it ("your sector’s pool card"). */
export const HelpAnchor = z.object({ id: HelpAnchorId, label: z.string() });
export type HelpAnchor = z.infer<typeof HelpAnchor>;

export const HelpTopic = z.object({
  id: z.string(),
  category: z.string(),
  title: z.string(),
  summary: z.string(),
  body: z.array(z.string()),
  steps: z.array(z.string()).optional(),
  keywords: z.string().optional(),
  related: z.array(z.string()).optional(),
  start: AppLink.optional(),
  /** What the topic explains on a page, for "Show me" when that page has it (APP-090). */
  show: z.array(HelpAnchorId).optional(),
});
export type HelpTopic = z.infer<typeof HelpTopic>;

export const HelpPageKey = z.string().regex(/^[a-z][a-z0-9-]{0,40}$/);

/** Browse help: works with AI off and sends nothing to a model. */
export const HelpTopics = z.object({
  page: z.object({
    key: z.string(),
    label: z.string(),
    topicIds: z.array(z.string()),
    /**
     * What Help may point at on this page for me (APP-090), by my roles. "Show me" still checks
     * the page itself before it highlights anything. Optional: older servers.
     */
    anchors: z.array(HelpAnchor).optional(),
  }),
  popularIds: z.array(z.string()),
  /** Every topic for my roles; page and popular ids point into it. */
  topics: z.array(HelpTopic),
  /**
   * Who answers questions: the people on the Stock Admin team (names only), the team's shared
   * mailbox when a System Admin set one, and whether a message can be sent from Help (not in
   * Preview).
   */
  contact: z.object({
    title: z.string(),
    body: z.string(),
    team: z.array(z.object({ id: z.string(), name: z.string() })),
    email: z.string().nullable(),
    canWrite: z.boolean(),
  }),
  /** Ask (chat) is available to me now. */
  chat: z.boolean(),
  /** Questions to start with on this page, when chat is on. */
  suggestions: z.array(z.string()),
});
export type HelpTopics = z.infer<typeof HelpTopics>;

export const HelpSearchResults = z.object({
  query: z.string(),
  topics: z.array(HelpTopic),
});
export type HelpSearchResults = z.infer<typeof HelpSearchResults>;

export const HelpAsk = z
  .object({
    question: z.string().trim().min(1).max(2000),
    page: HelpPageKey,
  })
  .strict();
export type HelpAsk = z.infer<typeof HelpAsk>;

export const HelpAnswer = z.object({
  paragraphs: z.array(z.string()),
  steps: z.array(z.string()),
  /** "Open Insights", "Budget pools": app paths only. */
  links: z.array(AppLink),
  /** Help topics the answer drew on ("How your pool is counted"). */
  topicIds: z.array(z.string()),
  /** It doesn't know, or the question is outside MyRewards: says who to ask instead. */
  unsure: z.boolean(),
  /** A message to the Stock Admin team it drafted; sent only when the person presses Send. */
  handoff: z.object({ subject: z.string(), message: z.string() }).nullable(),
  /**
   * "Show me": the thing on the asker's page the answer is about, checked against that page's
   * anchors for their roles (APP-090). Optional: answers kept from before it existed.
   */
  show: HelpAnchor.nullable().optional(),
  /** A how-to's start button ("Start a Team Achievement Award"), from the topic it used. */
  start: AppLink.nullable().optional(),
});
export type HelpAnswer = z.infer<typeof HelpAnswer>;

export const HelpMessage = z.object({
  id: z.string(),
  role: z.enum(['person', 'assistant']),
  text: z.string(),
  at: z.string(),
  answer: HelpAnswer.nullable(),
  feedback: z.enum(['up', 'down']).nullable(),
});
export type HelpMessage = z.infer<typeof HelpMessage>;

export const HelpConversation = z.object({
  conversationId: z.string().nullable(),
  messages: z.array(HelpMessage),
  /** Kept this many days, for me only. */
  historyDays: z.number().int().positive(),
});
export type HelpConversation = z.infer<typeof HelpConversation>;

export const HelpReply = z.object({
  conversationId: z.string(),
  question: HelpMessage,
  reply: HelpMessage,
});
export type HelpReply = z.infer<typeof HelpReply>;

export const AiFeedback = z.object({ rating: z.enum(['up', 'down']).nullable() }).strict();
export type AiFeedback = z.infer<typeof AiFeedback>;

export const AiFeedbackSaved = z.object({
  messageId: z.string(),
  rating: z.enum(['up', 'down']).nullable(),
});
export type AiFeedbackSaved = z.infer<typeof AiFeedbackSaved>;

export const HelpHandoff = z
  .object({
    subject: z.string().trim().min(1).max(200),
    message: z.string().trim().min(1).max(5000),
    /** Attach this Help conversation to the message. */
    conversationId: z.uuid().nullable(),
  })
  .strict();
export type HelpHandoff = z.infer<typeof HelpHandoff>;

export const HelpHandoffSent = z.object({
  /** The Stock Admin team members it went to. */
  to: z.array(z.object({ id: z.string(), name: z.string() })),
  at: z.string(),
});
export type HelpHandoffSent = z.infer<typeof HelpHandoffSent>;

// Ask the data (RPT5) lives in ./ask-data.ts (APP-191).

// ---- Simulated email to the assistant (APP-071) ----

/**
 * Whether this System Admin can write to the assistant as someone here, and its address. It takes
 * a test environment (mail kept as copies, never production) and Preview & testing on with them
 * named as a tester, the same control as Preview as.
 */
export const AssistantMailStatus = z.object({
  available: z.boolean(),
  /** Why not: the environment, Preview & testing is off, or they aren't named as a tester. */
  reason: z.enum(['environment', 'testing_off', 'not_tester']).nullable(),
  /** The address people write to (the shared mailbox), as the message is addressed. */
  address: z.string(),
});
export type AssistantMailStatus = z.infer<typeof AssistantMailStatus>;

/** A message as someone: a reply to an email in Sent mail, or a new one (then with a subject). */
export const SimulateAssistantMail = z
  .object({
    personId: z.uuid(),
    replyTo: z.uuid().nullable(),
    subject: z.string().trim().max(200).default(''),
    text: z.string().trim().min(1).max(10_000),
  })
  .strict()
  .refine((m) => m.replyTo !== null || m.subject.length > 0, {
    message: 'A new message needs a subject',
    path: ['subject'],
  });
export type SimulateAssistantMail = z.infer<typeof SimulateAssistantMail>;

export const SimulatedMailOutcome = z.enum([
  /** The assistant answered (read back, asked, refused or did it). */
  'answered',
  /** Still working past the wait: its answer arrives in Sent mail. */
  'working',
  /** The assistant couldn't read it (the model didn't answer). Nothing was done. */
  'failed',
  /** Turned away: the person isn't exactly one active person with that address. */
  'rejected',
  'ignored',
  /** APP-189: a reply to an email that went to someone else (a copy's reply-all): not answered. */
  'copy',
]);
export type SimulatedMailOutcome = z.infer<typeof SimulatedMailOutcome>;

export const SimulatedMailResult = z.object({
  outcome: SimulatedMailOutcome,
  /** The assistant's emails about this message, the sender's first, then any copies. */
  replies: z.array(
    z.object({
      id: z.uuid(),
      subject: z.string(),
      to: z.string(),
      /** A copy for the Stock Admin team (I5), not the answer to the sender. */
      copy: z.boolean(),
    }),
  ),
});
export type SimulatedMailResult = z.infer<typeof SimulatedMailResult>;
